import { apiClient } from '@api';
import { webEnvConfig } from '@config';
import { container } from '@containers';
import { LoginResponse, LogoutResponse } from '@libs/types';
import { AuthStore } from '@stores';

import { DI_TOKENS } from '../../../constants';

// The only SPA route with an OAuth callback handler (useKeycloakOAuthCallback).
// Hardcoded so the redirect_uri sent to Keycloak never depends on whatever
// path the user happens to be on when the login button is clicked.
export const SSO_REDIRECT_PATH = '/login';

export const getSSORedirectUri = (): string =>
  window.location.origin + SSO_REDIRECT_PATH;

export const ssoLogin = async (code: string, redirectUri: string) => {
  const { data } = await apiClient.post<LoginResponse>('api/auth/sso-login', {
    code,
    redirectUri,
  });
  return data;
};

export const keycloakGoogleSSOLoginURL = (locale?: string): void => {
  const authUrl = `${webEnvConfig.keycloak.authServerUrl}/realms/${webEnvConfig.keycloak.realm}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: webEnvConfig.keycloak.clientId,
    kc_idp_hint: 'google',
    redirect_uri: getSSORedirectUri(),
    prompt: 'select_account',
    scope: 'openid email profile ',
    ...(locale ? { ui_locales: locale, kc_locale: locale } : {}),
  });

  window.location.href = `${authUrl}?${params.toString()}`;
};

export const keycloakRealmLoginURL = (locale?: string): void => {
  const authUrl = `${webEnvConfig.keycloak.authServerUrl}/realms/${webEnvConfig.keycloak.realm}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: webEnvConfig.keycloak.clientId,
    redirect_uri: getSSORedirectUri(),
    scope: 'openid email profile ',
    ...(locale ? { ui_locales: locale, kc_locale: locale } : {}),
  });

  window.location.href = `${authUrl}?${params.toString()}`;
};

export const login = async (code: string, redirectUri: string) => {
  const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
  const response = await ssoLogin(code, redirectUri);
  authStore.setToken(response.accessToken);
  authStore.setRefreshToken(response.refreshToken);
  authStore.setUserProfile(response.userInfo);

  // Re-arm the one-time post-login Organization Profile nudge so it shows once
  // on each fresh sign-in. Its "seen" flag lives in sessionStorage, which
  // survives a logout→login within the same browser tab, so without this the
  // nudge never reappears after the first view. login() runs only on a real
  // authentication (not on reload/navigation), making it the correct re-arm point.
  // Canonical key: SEEN_KEY in
  // features/tenant-profile-hub/nudges/LoginCompletionTooltipHost.tsx
  sessionStorage.removeItem('orgProfileNudge:loginTooltipSeen');

  return response;
};

export const logout = async (): Promise<void> => {
  const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
  const refreshToken = authStore.refreshToken;

  // Clear auth state first to ensure user is logged out even if API call fails
  authStore.clearAuth();

  // Attempt to revoke refresh token on server (best effort)
  if (refreshToken) {
    try {
      try {
        await apiClient.post<LogoutResponse>('api/auth/logout', { refreshToken });
      } catch (error) {
        throw new Error(`Failed to logout from Keycloak: ${error}`);
      }
    } catch (error) {
      // Log but don't throw - user is already logged out locally
      console.warn('Failed to revoke refresh token on server:', error);
    }
  }
};
